import { memo, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useTranslation } from "react-i18next";
import RecordListComponent from "modules/record/record-list/components/RecordList";

import RelatedListRelationshipEditor from "./RelatedListRelationshipEditor";
import RelatedListSkeleton from "./RelatedListSkeleton";
import RelatedListToolbar from "./RelatedListToolbar";
import { useRelatedListRuntime } from "../hooks/useRelatedListRuntime";
import { ResolvedRelatedList } from "../types";
import {
    getRecordProducerPrefillParamKey,
    serializeRecordProducerPrefill,
} from "modules/record/record-producer/services/recordProducerPrefill";
import { appendReturnToParam } from "modules/record/shared/services/recordNavigationReturn";
import { navigateFromClick } from "shared/services/navigationClick";
import { useColumns } from "app/hooks/useColumns";
import { RecordManagement } from "app/class/record-management";
import { sectionPrefill } from "../services/sectionPrefill";

const RELATED_LIST_DEBUG_STORAGE_KEY = "debug.relatedList";

const isRelatedListDebugEnabled = () => {
    if (typeof window === "undefined") {
        return false;
    }

    return window.localStorage.getItem(RELATED_LIST_DEBUG_STORAGE_KEY) === "1";
};

interface RelatedListPanelProps {
    relatedList: ResolvedRelatedList;
    routeId: string;
}

const RelatedListPanel = ({
    relatedList,
    routeId,
}: RelatedListPanelProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const { data: effectiveColumns = [] } = useColumns({ enabled: relatedList.relationshipType === "inheritance_ancestors" });
    const { queryParams, bridgeRecords, reload, isLoading } = useRelatedListRuntime(relatedList, routeId);
    const relatedListDebugEnabled = isRelatedListDebugEnabled();

    if (relatedListDebugEnabled) {
        console.log(`[RelatedListDebugJSON] panel ${JSON.stringify({
            routeId,
            definitionId: relatedList.definition.id,
            definitionName: relatedList.definition.name,
            relationshipType: relatedList.relationshipType,
            tableName: relatedList.tableName,
            targetColumnName: relatedList.targetColumnName,
            allowNew: relatedList.allowNew,
            newMode: relatedList.newMode,
            allowEditRelationships: relatedList.allowEditRelationships,
            queryParams,
            bridge: relatedList.bridge ?? null,
            bridgeRecordCount: bridgeRecords.length,
            isLoading,
        })}`);
    }

    const handleNew = useCallback(async (event?: React.MouseEvent<HTMLElement>) => {
        if (relatedList.relationshipType === "many_to_many") {
            // "create_target" sends the user to create a brand new record on the
            // target table directly, skipping the link-existing-record popup.
            // The new record isn't auto-linked back through the bridge table --
            // the user still needs to open "Edit relationships" once it exists.
            if (relatedList.newMode !== "create_target") {
                setIsEditorOpen(true);
                return;
            }

            const target = `/home/workspace/${relatedList.tableName}/record`;
            navigateFromClick(event, navigate, appendReturnToParam(target, `${location.pathname}${location.search}${location.hash}`));
            return;
        }

        if (!relatedList.targetColumnName) {
            return;
        }

        const values = await sectionPrefill(relatedList.tableName, relatedList.targetColumnName, routeId,
            id => new RecordManagement<{ data?: { sys_table_id?: string | { id: string } } }>("sys_form_views").get(id));
        if (relatedList.tableName === "sys_choice" && relatedList.targetColumnName === "ref_column") {
            const column = await new RecordManagement<{ data?: { sys_table_id?: string | { id: string } } }>("sys_columns").get(routeId);
            const table = column?.data?.sys_table_id;
            values.ref_table = typeof table === "string" ? table : table?.id || "";
        }
        const prefill = serializeRecordProducerPrefill(values);

        const target = `/home/workspace/${relatedList.tableName}/record?${getRecordProducerPrefillParamKey()}=${prefill}`;
        navigateFromClick(event, navigate, appendReturnToParam(target, `${location.pathname}${location.search}${location.hash}`));
    }, [location.hash, location.pathname, location.search, navigate, relatedList.newMode, relatedList.relationshipType, relatedList.tableName, relatedList.targetColumnName, routeId]);

    const headerContent = useMemo(() => (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: 1,
                px: 1,
                pt: 0.5,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                <RelatedListToolbar
                    relatedList={relatedList}
                    onNew={handleNew}
                    onEditRelationships={() => setIsEditorOpen(true)}
                />
            </Box>
        </Box>
    ), [handleNew, relatedList]);

    if (isLoading) {
        return <RelatedListSkeleton />;
    }

    return (
        <>
            <RecordListComponent
                key={`${relatedList.definition.id}:${routeId}`}
                paramTableName={relatedList.tableName}
                queryParams={queryParams}
                isRelatedList
                columnPreferenceScope={`related.${relatedList.definition.id}`}
                headerContent={headerContent}
                readOnly={relatedList.relationshipType === "inheritance_ancestors"}
                columnLabels={relatedList.relationshipType === "inheritance_ancestors" ? { sys_table_id: String(t("inheritance.column.source_table")) } : undefined}
                actions={relatedList.relationshipType === "inheritance_ancestors" ? (params) => [
                    ...(effectiveColumns.some((column) => column.id === params.row.id && column.sys_table_id === routeId && Object.keys(column.overridden || {}).length > 0)
                        ? [<GridActionsCellItem key="customized-indicator" icon={<TuneOutlinedIcon color="primary" />}
                            label={String(t("override.customized"))} disabled />] : []),
                    <GridActionsCellItem
                        key="customize"
                        icon={<TuneOutlinedIcon />}
                        label={String(t(effectiveColumns.some((column) => column.id === params.row.id && column.sys_table_id === routeId && Object.keys(column.overridden || {}).length > 0)
                            ? "override.customized" : "inheritance.column.customize"))}
                        onClick={(event) => navigateFromClick(event, navigate,
                            appendReturnToParam(`/home/column-override/${routeId}/${params.row.id}`, `${location.pathname}${location.search}${location.hash}`))}
                        showInMenu
                    />,
                    <GridActionsCellItem
                        key="edit-parent"
                        icon={<EditOutlinedIcon />}
                        label={String(t("inheritance.column.edit_parent", { table: params.row?.sys_table_id?.display || params.row?.sys_table_id?.name || "" }))}
                        onClick={(event) => navigateFromClick(event, navigate,
                            appendReturnToParam(`/home/workspace/sys_columns/record/${params.row.id}`, `${location.pathname}${location.search}${location.hash}`))}
                        showInMenu
                    />,
                ] : undefined}
            />
            <RelatedListRelationshipEditor
                open={isEditorOpen}
                relatedList={relatedList}
                routeId={routeId}
                bridgeRecords={bridgeRecords}
                onClose={() => setIsEditorOpen(false)}
                onSaved={reload}
            />
        </>
    );
};

export default memo(RelatedListPanel);
